class TextImageColumnsSlider extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.js-slider-track');
    this.slides = this.querySelectorAll('.text-image-columns-slider__slide');
    this.prevButton = this.querySelector('.js-slider-prev');
    this.nextButton = this.querySelector('.js-slider-next');
    this.totalSlides = this.slides.length;
    this.flickity = null;
    
    this.init();
  }

  init() {
    if (this.totalSlides <= 1) return;
    
    // Load Flickity dynamically
    this.loadFlickity();
  }

  async loadFlickity() {
    try {
      // Import Flickity from the chunk file
      const { Flickity } = await import(window.flu?.chunks?.flickity || '/assets/flickity-chunk.js');
      
      this.flickity = new Flickity(this.track, {
        cellAlign: this.getCellAlign(),
        contain: false,
        draggable: true,
        freeScroll: false,
        wrapAround: true,
        prevNextButtons: false,
        pageDots: false,
        adaptiveHeight: false,
        groupCells: this.getGroupCells(),
        on: {
          ready: () => {
            this.setupCustomNavigation();
            this.updateNavigationVisibility();
          },
          change: () => {
            this.updateNavigationState();
          }
        }
      });

      // Handle resize
      window.addEventListener('resize', () => {
        setTimeout(() => {
          // Update cell alignment on resize
          this.flickity.options.cellAlign = this.getCellAlign();
          this.flickity.resize();
          this.updateNavigationVisibility();
        }, 150);
      });

    } catch (error) {
      console.error('Failed to load Flickity:', error);
      // Fallback to existing custom slider logic
      this.initCustomSlider();
    }
  }

  getCellAlign() {
    // Center alignment for mobile, left alignment for desktop
    if (window.innerWidth >= 768) {
      return 'left';
    } else {
      return 'center';
    }
  }

  getGroupCells() {
    // Group cells based on screen size for better mobile/desktop experience
    if (window.innerWidth >= 768) {
      return false; // Don't group on desktop for smooth scrolling
    } else {
      return 1; // Group by 1 on mobile
    }
  }

  setupCustomNavigation() {
    if (this.prevButton && this.nextButton) {
      this.prevButton.addEventListener('click', () => {
        this.flickity.previous();
      });
      
      this.nextButton.addEventListener('click', () => {
        this.flickity.next();
      });
    }
  }

  updateNavigationVisibility() {
    const navigation = this.querySelector('.text-image-columns-slider__navigation');
    if (navigation) {
      // Show navigation if there are more slides than visible
      const slidesPerView = this.getSlidesPerView();
      const showNavigation = this.totalSlides > slidesPerView;
      
      if (showNavigation) {
        navigation.style.display = 'flex';
        navigation.setAttribute('data-show-mobile', 'true');
        navigation.setAttribute('data-show-desktop', 'true');
      } else {
        navigation.style.display = 'none';
      }
    }
  }

  updateNavigationState() {
    // Since wrapAround is true, buttons are always enabled
    if (this.prevButton && this.nextButton) {
      this.prevButton.style.opacity = '1';
      this.nextButton.style.opacity = '1';
      this.prevButton.disabled = false;
      this.nextButton.disabled = false;
    }
  }

  getSlidesPerView() {
    if (window.innerWidth >= 768) {
      return 2.4;
    } else if (window.innerWidth >= 480) {
      return 2;
    } else {
      return 1;
    }
  }

  // Fallback custom slider (keeping original logic as backup)
  initCustomSlider() {
    this.currentSlide = 0;
    
    this.prevButton.addEventListener('click', () => this.prevSlide());
    this.nextButton.addEventListener('click', () => this.nextSlide());
    
    window.addEventListener('resize', () => this.updateSlider());
    
    this.updateSlider();
  }

  getSlideWidth() {
    const slidesPerView = this.getSlidesPerView();
    return 100 / slidesPerView;
  }

  updateSlider() {
    const slideWidth = this.getSlideWidth();
    const maxSlides = Math.ceil(this.totalSlides - this.getSlidesPerView() + 0.4);
    
    if (this.currentSlide > maxSlides) {
      this.currentSlide = maxSlides;
    }
    
    const translateX = -(this.currentSlide * slideWidth);
    this.track.style.transform = `translateX(${translateX}%)`;
    
    this.prevButton.style.opacity = this.currentSlide === 0 ? '0.5' : '1';
    this.nextButton.style.opacity = this.currentSlide >= maxSlides ? '0.5' : '1';
  }

  nextSlide() {
    const maxSlides = Math.ceil(this.totalSlides - this.getSlidesPerView() + 0.4);
    if (this.currentSlide < maxSlides) {
      this.currentSlide++;
      this.updateSlider();
    }
  }

  prevSlide() {
    if (this.currentSlide > 0) {
      this.currentSlide--;
      this.updateSlider();
    }
  }

  // Cleanup method
  destroy() {
    if (this.flickity) {
      this.flickity.destroy();
    }
  }
}

customElements.define('text-image-columns-slider', TextImageColumnsSlider);
